import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus, createChat, setDraftMessage} from 'llm/chat-library/ChatActions';
import {CreateChatOptions} from 'llm/chat-library/ChatDAO';
import {getChatTypeForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';

export interface CreateChatForPipelineStepResult {
  chatSessionId: string;
}

/**
 * Create a Pipelines Custom Code Writer chat targeting a pipeline step, for use inline in the Custom
 * Code editor.
 *
 * The chat is scoped to the step via `pipelineStepId`, which the backend persists into the chat's
 * application state at creation time (see `PipelinesCustomCodeWriterAgentDomainHandler`).
 *
 * We seed the draft's settings with the PIPELINES_CUSTOM_CODE_WRITER agent type so the composer
 * knows which agent to send to — that agent is not offered by the sidepanel's agent picker, so
 * `useMessageThread` would otherwise seed the wrong default agent for this empty chat.
 */
export default async function createChatForPipelineStep(
  pipelineStepId: string
): Promise<CreateChatForPipelineStepResult> {
  const agentType = AgentType.PIPELINES_CUSTOM_CODE_WRITER;
  const chatType = getChatTypeForNewAgentChat(agentType);
  const createOptions: CreateChatOptions = {
    [agentType]: {pipelineStepId},
  };
  const {chatSessionId} = await createChat(chatType, createOptions, agentType);
  trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CREATED_CHAT_FOR_PIPELINE_STEP, {
    agentType,
    pipelineStepId,
    chatSessionId,
  });
  setDraftMessage(chatSessionId, {
    status: ChatDraftStatus.DRAFT,
    message: {text: '', selectedFiles: []},
    settings: {agentType, publicDataEnabled: false},
  });
  return {chatSessionId};
}
